import {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import DotsLoading from '../../../components/dotsLoading';
import ChatHeaderIcon from '../../../components/mazraeKit/headers/ChatHeaderIcon/ChatHeaderIcon';
import CustomHeader from '../../../components/mazraeKit/headers/homeHeader';
import NotificationHeaderIcon from '../../../components/mazraeKit/headers/NotificationHeaderIcon/NotificationHeaderIcon';
import NoItemFoundPage from '../../../components/noItemFoundPage';
import SpinnerLoading from '../../../components/SpinnerLoading';
import {
  IStoreOrder,
  OrderStepTypes,
} from '../../../services/hookService/getUserOrderList/types';
import useGetUserOrderList from '../../../services/hookService/getUserOrderList/useGetUserOrderList';
import MyOrderItem from './components/MyOrderItem';
import OrderAdminPendingSheet from './components/OrderAdminPendingSheet';
import OrderRegisterSuccessSheet from './components/OrderRegisterSuccessSheet';
import {tabsConfig} from './components/tabsConfig';
import TabSelector from './components/TabSelector';
import MainFooter from '../../../components/mazraeKit/footers/footerMain';

interface StoreOrderCardData {
  parentOrderGuid: string;
  storeOrder: IStoreOrder;
}

const MyOrders = () => {
  const navigate = useNavigate();
  const {state, search} = useLocation();

  const searchParams = new URLSearchParams(search);
  const tabFromUrl = searchParams.get('tab') as OrderStepTypes | null;

  const isValidTab = tabsConfig.some(t => t.key === tabFromUrl);

  const [selectedTab, setSelectedTab] = useState<OrderStepTypes>(
    isValidTab ? tabFromUrl! : 'incomplete',
  );

  const [openPendingBottomSheet, setOpenPendingBottomSheet] =
    useState<boolean>(false);

  const {data, loaderRef, isFetchingNextPage, isLoading} = useGetUserOrderList({
    order_step: selectedTab,
  });

  const shouldOpenOnce = state?.isOrderRegisterSuccess ?? false;

  useEffect(() => {
    if (state?.isOrderRegisterSuccess) {
      navigate('.', {replace: true, state: {}});
    }
  }, [state, navigate]);

  /**
   * ONE CARD PER STORE ORDER
   * NO FLATTENING
   */
  const storeOrderCards: StoreOrderCardData[] = useMemo(() => {
    if (!data?.length) return [];

    return data.flatMap(order =>
      order.store_order.map(storeOrder => ({
        parentOrderGuid: order.guid,
        storeOrder,
      })),
    );
  }, [data]);

  // sync tab changes to URL and fill out the tab search param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', selectedTab);

    navigate({search: params.toString()}, {replace: true});
  }, [selectedTab]);

  return (
    <div className="flex flex-col w-full bg-white h-dvh">
      <CustomHeader
        preComponent={() => (
          <div
            className="flex relative gap-2 items-center"
            onClick={() => navigate('/my-account')}
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
            <span className="font-medium">سفارش‌های من</span>
          </div>
        )}
        postComponent={() => (
          <div className="flex gap-3">
            <NotificationHeaderIcon />
            <ChatHeaderIcon />
          </div>
        )}
      />

      {/* Page Content */}
      <div className="flex overflow-y-auto flex-col gap-3 px-6 py-3 w-full h-full">
        <TabSelector
          tabsConfig={tabsConfig}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />

        {isLoading && (
          <div className="flex justify-center items-center h-screen">
            <SpinnerLoading size={40} />
          </div>
        )}

        {!isLoading && !storeOrderCards.length && <NoItemFoundPage />}

        {!isLoading &&
          storeOrderCards.map(({storeOrder, parentOrderGuid}) => (
            <MyOrderItem
              key={storeOrder.guid}
              storeOrder={storeOrder}
              parentOrderGuid={parentOrderGuid}
              selectedTab={selectedTab}
              setOpenPendingBottomSheet={setOpenPendingBottomSheet}
            />
          ))}

        <div ref={loaderRef} className="mt-4 text-center">
          {isFetchingNextPage ? <DotsLoading /> : null}
        </div>
      </div>

      <MainFooter />

      <OrderRegisterSuccessSheet shouldOpen={shouldOpenOnce} />

      <OrderAdminPendingSheet
        shouldOpen={openPendingBottomSheet}
        setShouldOpen={setOpenPendingBottomSheet}
      />
    </div>
  );
};

export default MyOrders;
